import type { GeocodeResponse } from '#constants/types/norce';
import useApiFetch from '~/composables/useApiFetch';
import { useGlobalContentStore } from '~/store/globalContent';
import { useLocationStore } from '~/store/location';

/**
 * Search for a postcode/town (or use the browser position) and save the result
 * in the shared location store.
 */
export default function useLocationSearch() {
  const globalContent = useGlobalContentStore();
  const locationStore = useLocationStore();
  const { apiGet } = useApiFetch();

  const searchTerm = ref('');
  const searching = ref(false);
  const locating = ref(false);
  const errorKey = ref('');

  const searchByTerm = async(): Promise<boolean> => {
    const term = searchTerm.value.trim();
    if (!term || searching.value) {
      return false;
    }
    searching.value = true;
    errorKey.value = '';
    const res = await apiGet<GeocodeResponse>(`/geocode?address=${encodeURIComponent(term)}&country=${globalContent.getCountryCode}`).catch(() => null);
    searching.value = false;
    if (res?.lat && res?.lng) {
      locationStore.setLocation({ lat: res.lat, lng: res.lng, label: term });
      return true;
    }
    errorKey.value = 'pdp.experience.storePanel.notFound';
    return false;
  };

  const useMyPosition = () => {
    errorKey.value = '';
    if (!navigator.geolocation) {
      errorKey.value = 'pdp.experience.storePanel.locationError';
      return;
    }
    locating.value = true;
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        locating.value = false;
        locationStore.setLocation({ lat: coords.latitude, lng: coords.longitude, label: '' });
      },
      () => {
        locating.value = false;
        errorKey.value = 'pdp.experience.storePanel.locationError';
      },
      { timeout: 10000 }
    );
  };

  return { searchTerm, searching, locating, errorKey, searchByTerm, useMyPosition };
}
